import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../common/Util.css";
import "../../common/skeleton-effect.css";
import CommonDropdown from "../../common/common_dropDown";
import { Row, Col, Form, Button } from "react-bootstrap";
import { FiPlus, FiArrowLeft } from "react-icons/fi";
import { Modal } from "react-bootstrap";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { saveVendorAPI, updateVendorAPI, getVendorEditApi, addAttachmentApi } from "./vendor-api";
import LoaderModal from "../../common/loader";
import CommonSnackbar from "../../common/reusable-commponent";
import { sanitizeInput } from "../../common/security-utils";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const LocationPicker = ({ setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });
  return null;
};

const VendorCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.state?.id;

  const [isLoader, setIsLoader] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });
  const triggerSnackbar = (message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  };
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, show: false }));
  };

  // Vendor Details State
  const [vendorId, setVendorId] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [type, setType] = useState("");
  const [totalPurchaseAmount, setTotalPurchaseAmount] = useState("");

  // Bank Account Details State
  const [accName, setAccName] = useState("");
  const [accNumber, setAccNumber] = useState("");
  const [gstNo, setGstNo] = useState("");
  const [ifscCode, setIfscCode] = useState("");

  // Config & Description State
  const [loginRequire, setLoginRequire] = useState(false);
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState("");
  const [attachmentRemoved, setAttachmentRemoved] = useState(false);

  // Billing Address State
  const [fromAddress, setFromAddress] = useState("");
  const [fromCountryOption, setFromCountryOption] = useState(null);
  const [fromStateOption, setFromStateOption] = useState(null);
  const [fromCityOption, setFromCityOption] = useState(null);
  const [fromZipCode, setFromZipCode] = useState("");
  const [addr1, setAddr1] = useState({ lat: "", lng: "" });

  // Map State
  const [showMap, setShowMap] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  const [isSearching, setIsSearching] = useState(false);
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(sanitizeInput(searchQuery))}&limit=5`);
      const data = await response.json();
      setSearchResults(data);
    } catch {
      triggerSnackbar("Error searching location. Please try again.", false);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  const handleSelectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    if (isNaN(lat) || isNaN(lng)) return;
    setMapCenter([lat, lng]);
    setMapPosition({ lat, lng });
    setSearchResults([]);
    setSearchQuery(sanitizeInput(result.display_name));
  };
  const [errorMsg, setErrorMsg] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setIsLoadingData(true);
        try {
          const response = await getVendorEditApi(id);
          if (response.data && response.data.success) {
            const data = response.data.vendors || response.data.data || response.data;
            setVendorId(sanitizeInput(String(data.vendor_id || "")));
            setVendorName(sanitizeInput(String(data.vendor_name || "")));
            setMobile(sanitizeInput(String(data.mobilenumber || "")));
            setEmail(sanitizeInput(String(data.email || "")));
            setType(sanitizeInput(String(data.type || "")));
            setTotalPurchaseAmount(sanitizeInput(String(data.total_purchase_amount || "")));

            setAccName(sanitizeInput(String(data.bank_account_name || "")));
            setAccNumber(sanitizeInput(String(data.bank_account_number || "")));
            setGstNo(sanitizeInput(String(data.gst_number || "")));
            setIfscCode(sanitizeInput(String(data.ifsc_code || "")));

            setLoginRequire(data.login_require === true || data.login_require === "true" || data.login_require === 1);
            setDescription(sanitizeInput(String(data.description || "")));

            setFromAddress(sanitizeInput(String(data.billing_address || "")));
            setFromCountryOption({
              label: sanitizeInput(String(data.billing_country || "")),
              value: data.billing_country_id,
            });
            setFromStateOption({
              label: sanitizeInput(String(data.billing_state || "")),
              value: data.billing_state_id,
            });
            setFromCityOption({
              label: sanitizeInput(String(data.billing_city || "")),
              value: data.billing_city_id,
            });
            setFromZipCode(sanitizeInput(String(data.billing_zipcode || "")));
            setAddr1({
              lat: data.billing_latitude,
              lng: data.billing_longitude,
            });
            setAttachment(data.upload || "");
          } else {
            triggerSnackbar(response.data?.message || "Failed to fetch vendor data", false);
          }
        } catch {
          triggerSnackbar("An error occurred while fetching data", false);
        } finally {
          setIsLoadingData(false);
        }
      }
    };
    fetchData();
  }, [id]);

  const handleMapConfirm = () => {
    if (mapPosition) {
      const lat = mapPosition.lat.toFixed(6);
      const lng = mapPosition.lng.toFixed(6);
      setAddr1({ lat, lng });
      setShowMap(false);
      setMapPosition(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachment(reader.result);
        setAttachmentRemoved(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const submitForm = async () => {
    setIsLoader(true);
    try {
      const payload = {
        vendor_id: vendorId ?? "",
        vendor_name: vendorName ?? "",
        mobilenumber: mobile ?? "",
        email: email ?? "",
        bank_account_name: accName ?? "",
        bank_account_number: accNumber ?? "",
        gst_number: gstNo ?? "",
        type: type ?? "",
        login_require: loginRequire,
        billing_address: fromAddress ?? "",
        billing_country_id: fromCountryOption?.value,
        billing_state_id: fromStateOption?.value,
        billing_city_id: fromCityOption?.value,
        billing_zipcode: fromZipCode ?? "",
        description: description ?? "",
        attachment_removed: attachmentRemoved,
        billing_latitude: addr1.lat,
        billing_longitude: addr1.lng,
        ifsc_code: ifscCode ?? "",
      };

      let response;
      if (id) {
        response = await updateVendorAPI(id, payload);
      } else {
        response = await saveVendorAPI(payload);
      }

      if (response && response.data && response.data.success) {
        const newVendorId = response.data.id || id;
        if (attachment && !attachmentRemoved) {
          await addAttachmentApi(newVendorId, { attachment: attachment });
        }
        triggerSnackbar(response.data.message || "Vendor saved successfully", true);
        setTimeout(() => navigate(-1), 1000);
      } else {
        triggerSnackbar(response.data?.message || "Failed to save vendor", false);
        if (response.data && response.data.error) {
          setErrorMsg(response.data.error);
        }
      }
    } catch {
      triggerSnackbar("An error occurred while submitting the form", false);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div className="mt-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <Button variant="link" className="p-0 text-dark" onClick={() => navigate(-1)}>
              <FiArrowLeft size={20} />
            </Button>
            <h4 className="fw-600 m-0" style={{ fontSize: "20px" }}>
              {id ? "Edit Vendor" : "New Vendor"}
            </h4>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="light"
              className="bg-white border fw-500"
              style={{
                color: "#495057",
                borderRadius: "20px",
                padding: "6px 20px",
              }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="fw-500"
              style={{
                backgroundColor: "#3469E7",
                borderColor: "#3469E7",
                borderRadius: "20px",
                padding: "6px 20px",
              }}
              onClick={submitForm}
              disabled={isLoader}
            >
              {isLoader ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {isLoadingData ? (
          <Row className="g-3">
            <Col md={8}>
              <div className="bg-white p-4 rounded shadow-sm mb-3">
                <div className="skeleton skeleton-text-lg mb-4" style={{ width: "150px" }} />
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div className="skeleton skeleton-avatar" style={{ borderRadius: "50%" }} />
                  <div className="skeleton skeleton-button" />
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "80px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "100px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "110px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "70px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                </Row>
              </div>

              <div className="bg-white p-3 rounded shadow-sm">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="skeleton skeleton-text-lg" style={{ width: "130px" }} />
                  <div className="d-flex gap-2">
                    <div className="skeleton" style={{ width: "100px", height: "38px", borderRadius: "8px" }} />
                    <div className="skeleton" style={{ width: "100px", height: "38px", borderRadius: "8px" }} />
                    <div className="skeleton skeleton-button" style={{ width: "110px" }} />
                  </div>
                </div>
                <Row className="g-3">
                  <Col md={12}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "70px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "50px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "70px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                  <Col md={6}>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "75px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </Col>
                </Row>
              </div>
            </Col>

            <Col md={4}>
              <div className="d-flex flex-column gap-3">
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="skeleton skeleton-text-lg mb-4" style={{ width: "180px" }} />
                  <div className="mb-3">
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "110px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </div>
                  <div className="mb-3">
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "125px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </div>
                  <div className="mb-3">
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "80px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </div>
                  <div>
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "95px" }} />
                    <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
                  </div>
                </div>

                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="skeleton skeleton-text-lg mb-4" style={{ width: "100px" }} />
                  <div className="skeleton skeleton-text-sm mb-2" style={{ width: "90px" }} />
                  <div className="skeleton" style={{ height: "90px", borderRadius: "8px" }} />
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <Row className="g-3">
            <Col md={8}>
              <div className="bg-white p-4 rounded shadow-sm mb-3 text-start">
                <h5 className="fw-600 mb-4" style={{ fontSize: "16px" }}>
                  Vendor details
                </h5>
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div className="rounded-circle bg-light d-flex align-items-center justify-content-center overflow-hidden" style={{ width: "64px", height: "64px", color: "#adb5bd" }}>
                    {attachment ? (
                      <img
                        src={attachment}
                        alt="Vendor"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <input type="file" id="vendor-photo" className="d-none" accept="image/*" onChange={handleFileChange} />
                    <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-1 rounded-pill px-3" onClick={() => document.getElementById("vendor-photo").click()}>
                      <FiPlus /> {attachment ? "Change photo" : "Add photo"}
                    </Button>
                  </div>
                </div>

                <Row className="g-3 text-start">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                        Vendor ID<span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control type="text" placeholder="Enter here..." maxLength={20} className="fs-13 rounded-3" value={vendorId} onChange={(e) => setVendorId(e.target.value)} />
                      {errorMsg.vendor_id && <span className="text-danger fs-12">{sanitizeInput(errorMsg.vendor_id)}</span>}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                        Vendor Name<span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control type="text" placeholder="Enter here..." maxLength={100} className="fs-13 rounded-3" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
                      {errorMsg.vendor_name && <span className="text-danger fs-12">{sanitizeInput(errorMsg.vendor_name)}</span>}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                        Mobile Number<span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control type="text" placeholder="Enter here..." maxLength={10} className="fs-13 rounded-3" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} />
                      {errorMsg.mobilenumber && <span className="text-danger fs-12">{sanitizeInput(errorMsg.mobilenumber)}</span>}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                        Email
                      </Form.Label>
                      <Form.Control type="text" placeholder="Enter here..." maxLength={100} className="fs-13 rounded-3" value={email} onChange={(e) => setEmail(e.target.value)} />
                      {errorMsg.email && <span className="text-danger fs-12">{sanitizeInput(errorMsg.email)}</span>}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                        Type
                      </Form.Label>
                      <Form.Select className="fs-13 text-muted rounded-3" value={type} onChange={(e) => setType(e.target.value)}>
                        <option value="">Select</option>
                        <option value="Raw Material">Raw Material</option>
                        <option value="Service">Service</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      {errorMsg.type && <span className="text-danger fs-12">{sanitizeInput(errorMsg.type)}</span>}
                    </Form.Group>
                  </Col>
                  {/* <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                        Total Purchase Amount
                      </Form.Label>
                      <Form.Control type="number" placeholder="Enter here..." className="fs-13 rounded-3" value={totalPurchaseAmount} onChange={(e) => setTotalPurchaseAmount(e.target.value)} />
                      {errorMsg.total_purchase_amount && <span className="text-danger fs-12">{errorMsg.total_purchase_amount}</span>}
                    </Form.Group>
                  </Col> */}
                </Row>
              </div>

              {/* Billing Address Card */}
              <div className="bg-white p-3 rounded shadow-sm text-start">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h5 className="fw-600 mb-0" style={{ fontSize: "16px" }}>
                    Billing address
                  </h5>

                  <div className="d-flex align-items-center gap-2 px-2 py-2 rounded" style={{ background: "#F6F8FC" }}>
                    <Form.Control
                      type="text"
                      placeholder="Latitude"
                      className="fs-13"
                      value={addr1.lat}
                      readOnly
                      style={{
                        width: "100px",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    />

                    <Form.Control
                      type="text"
                      placeholder="Longitude"
                      className="fs-13"
                      value={addr1.lng}
                      readOnly
                      style={{
                        width: "100px",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    />

                    <div
                      style={{
                        width: "1px",
                        height: "30px",
                        background: "#dcdfe6",
                        margin: "0 6px",
                      }}
                    />
                    <Button
                      variant="primary"
                      className="fs-13 fw-500 d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: "#3469E7",
                        borderColor: "#3469E7",
                        borderRadius: "20px",
                        padding: "6px 18px",
                      }}
                      onClick={() => {
                        setMapPosition(null);
                        setShowMap(true);
                      }}
                    >
                      Pin in map
                    </Button>
                  </div>
                </div>

                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                        Address<span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control type="text" placeholder="Enter here..." maxLength={255} className="fs-13 rounded-3" value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} />
                      {errorMsg.billing_address && <span className="text-danger fs-12">{sanitizeInput(errorMsg.billing_address)}</span>}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <div className="col-12 d-flex flex-column align-items-start">
                      <div className="form-label fs-13 fw-500 my-0 mb-1" style={{ color: "#28303F" }}>
                        City<span style={{ color: "red" }}>*</span>
                      </div>
                      <div className="w-100">
                        <CommonDropdown
                          apiUrl={`${adminUrl}get_city?state_id=${fromStateOption != null ? fromStateOption.value : ""}`}
                          placeholder="Select"
                          value={fromCityOption}
                          onSelect={(item) => {
                            setFromCityOption(item);
                            setFromStateOption({
                              value: item.state_id,
                              label: item.state_name,
                            });
                            setFromCountryOption({
                              value: item.country_id,
                              label: item.country_name,
                            });
                          }}
                        />
                      </div>
                      {errorMsg.billing_city_id && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {sanitizeInput(errorMsg.billing_city_id)}
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="col-12 d-flex flex-column align-items-start ps-0">
                      <div className="form-label fs-13 fw-500 my-0 mb-1" style={{ color: "#28303F" }}>
                        State<span style={{ color: "red" }}>*</span>
                      </div>
                      <div className="w-100">
                        <CommonDropdown
                          apiUrl={`${adminUrl}get_state?country_id=${fromCountryOption != null ? fromCountryOption.value : ""}`}
                          placeholder="Select"
                          value={fromStateOption}
                          onSelect={(item) => {
                            setFromStateOption(item);
                            setFromCountryOption({
                              value: item.country_id,
                              label: item.country_name,
                            });
                          }}
                        />
                      </div>
                      {errorMsg.billing_state_id && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {sanitizeInput(errorMsg.billing_state_id)}
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="col-12 d-flex flex-column align-items-start">
                      <div className="form-label fs-13 fw-500 my-0 mb-1" style={{ color: "#28303F" }}>
                        Country<span style={{ color: "red" }}>*</span>
                      </div>
                      <div className="w-100">
                        <CommonDropdown
                          apiUrl={`${adminUrl}get_country`}
                          placeholder="Select"
                          value={fromCountryOption}
                          onSelect={(item) => {
                            setFromCountryOption(item);
                          }}
                        />
                      </div>
                      {errorMsg.billing_country_id && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {sanitizeInput(errorMsg.billing_country_id)}
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                        Zip Code<span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control type="text" placeholder="Enter here..." maxLength={10} className="fs-13 rounded-3" value={fromZipCode} onChange={(e) => setFromZipCode(e.target.value)} />
                      {errorMsg.billing_zipcode && <span className="text-danger fs-12">{sanitizeInput(errorMsg.billing_zipcode)}</span>}
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Right Column - Bank, Description, Config */}
            <Col md={4}>
              <div className="d-flex flex-column gap-3">
                {/* Bank Account Details Card */}
                <div className="bg-white p-4 rounded shadow-sm text-start">
                  <h5 className="fw-600 mb-4" style={{ fontSize: "16px" }}>
                    Bank Account details
                  </h5>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                          Account Name
                        </Form.Label>
                        <Form.Control type="text" placeholder="Enter here..." maxLength={100} className="fs-13 rounded-3" value={accName} onChange={(e) => setAccName(e.target.value)} />
                        {errorMsg.bank_account_name && <span className="text-danger fs-12">{sanitizeInput(errorMsg.bank_account_name)}</span>}
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                          Account Number
                        </Form.Label>
                        <Form.Control type="text" placeholder="Enter here..." maxLength={18} className="fs-13 rounded-3" value={accNumber} onChange={(e) => setAccNumber(e.target.value.replace(/\D/g, "").slice(0, 18))} />
                        {errorMsg.bank_account_number && <span className="text-danger fs-12">{sanitizeInput(errorMsg.bank_account_number)}</span>}
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                          IFSC Code
                        </Form.Label>
                        <Form.Control type="text" placeholder="Enter here..." maxLength={11} className="fs-13 rounded-3" value={ifscCode} onChange={(e) => setIfscCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 11))} />
                        {errorMsg.ifsc_code && <span className="text-danger fs-12">{sanitizeInput(errorMsg.ifsc_code)}</span>}
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                          GST Number
                        </Form.Label>
                        <Form.Control type="text" placeholder="Enter here..." maxLength={20} className="fs-13 rounded-3" value={gstNo} onChange={(e) => setGstNo(e.target.value)} />
                        {errorMsg.gst_number && <span className="text-danger fs-12">{sanitizeInput(errorMsg.gst_number)}</span>}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Description Card */}
                <div className="bg-white p-4 rounded shadow-sm text-start">
                  <h5 className="fw-600 mb-4" style={{ fontSize: "16px" }}>
                    Description
                  </h5>
                  <Form.Group>
                    <Form.Label className="fw-500 fs-13 mb-1" style={{ color: "#28303F" }}>
                      Description
                    </Form.Label>
                    <Form.Control as="textarea" rows={3} placeholder="Enter here..." maxLength={500} className="fs-13 rounded-3" value={description} onChange={(e) => setDescription(e.target.value)} />
                  </Form.Group>
                </div>

                {/* Configuration Card */}
                {/* <div className="bg-white p-4 rounded shadow-sm text-start">
                    <h5 className="fw-600 mb-4" style={{ fontSize: "16px" }}>
                      Configuration
                    </h5>
                    <Form.Check
                      type="checkbox"
                      id="loginRequire"
                      label="Login Required"
                      className="fs-13"
                      checked={loginRequire}
                      onChange={(e) => setLoginRequire(e.target.checked)}
                    />
                  </div> */}
              </div>
            </Col>
          </Row>
        )}
      </div>
      {/* Map Modal */}
      <Modal show={showMap} onHide={() => setShowMap(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Location</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "500px" }}>
          <div className="mb-3">
            <div className="d-flex gap-2">
              <div style={{ position: "relative", flex: 1 }}>
                <Form.Control
                  type="text"
                  placeholder="Search for a location (e.g., Mumbai, India or specific address)"
                  maxLength={200}
                  className="fs-13"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />

                {searchResults.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 1000,
                      marginTop: "4px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {searchResults.map((result, index) => (
                      <div
                        key={index}
                        className="p-2 cursor-pointer"
                        style={{
                          borderBottom: index < searchResults.length - 1 ? "1px solid #f1f3f5" : "none",
                          cursor: "pointer",
                        }}
                        onClick={() => handleSelectSearchResult(result)}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
                      >
                        <div className="fs-13 fw-500">{sanitizeInput(result.display_name)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="primary" onClick={handleSearch} disabled={isSearching || !searchQuery.trim()} style={{ backgroundColor: "#3469E7", borderColor: "#3469E7" }}>
                {isSearching ? "Searching..." : "Search"}
              </Button>
            </div>
          </div>
          <MapContainer center={mapCenter} zoom={5} style={{ height: "90%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
            <LocationPicker setPosition={setMapPosition} />
            {mapPosition && <Marker position={mapPosition} />}
          </MapContainer>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMap(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleMapConfirm} disabled={!mapPosition}>
            Confirm Location
          </Button>
        </Modal.Footer>
      </Modal>
      <CommonSnackbar show={snackbar.show} message={snackbar.message} isSuccess={snackbar.status} onClose={handleSnackbarClose} />
      <LoaderModal show={isLoader} />
    </div>
  );
};

export default VendorCreate;
